import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import Navbar from "../../Layouts/Navbar/Navbar";
import fileFolderConfig from "../../commonFunctions/fileFolderConfig";
import LeftSideBar, { FolderCollapse } from "../../components/SideBar/LeftSideBar";
import { useAPIContext } from "../../context/APIContext";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { useModalContext } from "../../context/ModalContext";
import { useNavigationContext } from "../../context/NavigationContext";
import { useTreeContext } from "../../context/TreeDataContext";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import { useTableContext } from "../../context/TableContext";
import ResizableComponent from "../../Layouts/ResizableComponent";
import HeaderModes from "../../Layouts/Navbar/HeaderModes";
import { toastSuccess } from "../../utils/toastUtils";
import Loader from "../../Layouts/Loader/Loader";
import { deleteFolderOfFile, editFolderOrFile, insertNewFileOrFolder } from "../../utils/treeData/folderEditorUtils";
import { apiConfig } from "../../utils/Config/DefaultConfig/apiConfig";
import CreateWebhook from "./WebHook/CreateWebhook";
import ErrorMessage from "../../components/FormComponents/ErrorMessage";
import Button from "../../components/FormComponents/Button";
import useFetchData from "../../hooks/useFetchData";
import SingleSelectBox from "../../components/FormComponents/SingleSelectBox";
import Input from "../../components/FormComponents/Input";

const ApiSection = () => {
  const { fetchAllApiInOut, selectedApi, setSelectedApi } = useAPIContext();
  const { apiExplorerData, setApiExplorerData } = useTreeContext();
  const { getUserConfirmationForDelete } = useModalContext();
  const { saveChangesAndNavigate } = useNavigationContext();
  const { getAllTableData, getAllRdbmsData } = useTableContext();

  const [createApiCredentials, setCreateApiCredentials] = useState(null);
  const [isLoader, setIsLoader] = useState(true);
  const [isError, setIsError] = useState(false);

  const navigate = useNavigate();
  const { apiId } = useParams();

  const getEndpointConfig = (folder) => {
    const { parentData, id, fileInfo } = folder;
    const isWebhook = parentData === "webhook";
    const isV2 = fileInfo?.version === "v2";

    if (isWebhook) {
      return {
        endpoint: isV2 ? `/webhook-config/${id}` : `/webhook/${id}`,
        method: isV2 ? "put" : "patch"
      };
    }
    if (parentData === "activity-webhook") {
      return {
        endpoint: `/activity-webhook/${id}`,
        method: "patch"
      };
    }

    return {
      endpoint: `/${parentData}/${id}`,
      method: "put"
    };
  };

  const handleInsert = async (config, folderData, endpoint) => {
    try {
      const { data, message } = await axiosInstance.post(endpoint, config);
      const newItem = fileFolderConfig.getFileConfig(data.id, data.name, folderData.id, true, true, {
        version: data?.version || "v1"
      });
      const newTree = insertNewFileOrFolder(apiExplorerData, folderData, newItem);
      setApiExplorerData(newTree);
      setCreateApiCredentials(null);
      navigate(`./${data.id}`);
      toastSuccess(message);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleNewInsert = async (folderData, name) => {
    const { insert_type, id } = folderData;
    if (insert_type === "inline") {
      if (id === "http-request-templates") {
        await handleInsert(
          {
            ...apiConfig.http_request_templates,
            name: name
          },
          folderData,
          `/http-request-templates`
        );
        return;
      }
    }
    setCreateApiCredentials(folderData); // toggling the webhook popup
  };

  const createWebhook = async (config, folderData = createApiCredentials) => {
    try {
      const { id } = folderData;
      const { version } = config;
      if (id === "webhook") {
        const endpoint = version === "v1" ? "webhook" : "webhook-config";
        await handleInsert(config, folderData, endpoint);
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleDelete = async (folder) => {
    try {
      const userConfirmed = await getUserConfirmationForDelete(folder.name);
      if (!userConfirmed) return;

      const { endpoint } = getEndpointConfig(folder);
      const { message } = await axiosInstance.delete(endpoint);

      const newTree = deleteFolderOfFile(apiExplorerData, folder);
      setApiExplorerData(newTree);
      if (folder.id === selectedApi?.id) navigate(`./`);

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };
  const createActivityWebhook = (data) => {
    const newFile = fileFolderConfig.getFileConfig(data.id, data.name, "activity-webhook", true, true);
    const newTree = insertNewFileOrFolder(apiExplorerData, createApiCredentials, newFile);
    setApiExplorerData(newTree);
    setCreateApiCredentials(null);
    navigate(`./${data.id}`);
  };

  const handleEdit = async (folder, newName) => {
    const { endpoint, method } = getEndpointConfig(folder);
    const config = { name: newName };
    try {
      const response = await axiosInstance[method](endpoint, config);
      const { message } = response;

      const newTree = editFolderOrFile(apiExplorerData, folder, newName);
      setApiExplorerData(newTree);

      if (folder.id === selectedApi?.id) {
        setSelectedApi({ ...selectedApi, name: newName });
      }

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const callbackFunction = (id) => {
    if (id === selectedApi.id) return;
    saveChangesAndNavigate(id);
  };

  const initialFetching = async () => {
    setIsLoader(true);
    setIsError(false);
    try {
      await getAllTableData();
      await getAllRdbmsData();
      if (apiExplorerData && apiExplorerData.length > 0) {
        if (selectedApi.id && !apiId) {
          setSelectedApi({ id: "" });
        }
      } else {
        await fetchAllApiInOut();
      }
    } catch (error) {
      handleApiError(error);
      setIsError(true);
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => {
    initialFetching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <section className="main_section">
        <div className="sub_section">
          <ResizableComponent
            resizeSide="right"
            storageKey="left_sidebar_size"
            defaultSize={244}
            range={[244, 400]} // Specify the range as [minSize, maxSize]
            collapsible={true}
          >
            <HeaderModes />
            <div className="d_flex">
              <Navbar />
              {isError ? (
                <div className="d_flex align_center justify_center flex_column">
                  <ErrorMessage className={"text_center mb_3"}>Failed to load templates. Please try again</ErrorMessage>
                  <Button onClick={initialFetching}>Retry</Button>
                </div>
              ) : (
                <LeftSideBar
                  explorerData={apiExplorerData}
                  setExplorerData={setApiExplorerData}
                  selectedScreen={selectedApi}
                  handleNewInsert={handleNewInsert}
                  handleDelete={handleDelete}
                  handleEdit={handleEdit}
                  callbackFunction={callbackFunction}
                  type="API"
                  isLoader={isLoader}
                >
                  <div className="d_flex align_center gap_2">
                    <FolderCollapse explorerData={apiExplorerData} setExplorerData={setApiExplorerData} type="api" />
                  </div>
                </LeftSideBar>
              )}
            </div>
          </ResizableComponent>
          {isLoader ? <Loader /> : <Outlet />}
        </div>
        <BuilderModal
          isOpen={createApiCredentials}
          onClose={() => setCreateApiCredentials(null)}
          modalName="Create Webhook"
        >
          {createApiCredentials?.id === "activity-webhook" && (
            <CreateActivityWebhook createActivityWebhook={createActivityWebhook} />
          )}
          {createApiCredentials?.id === "webhook" && <CreateWebhook createWebhook={createWebhook} />}
        </BuilderModal>
      </section>
    </>
  );
};

const CreateActivityWebhook = ({ createActivityWebhook }) => {
  const { data, loading, error } = useFetchData("/state-machines");
  const [selection, setSelection] = useState({
    name: "",
    state_machine_id: "",
    activity_id: ""
  });

  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const create = async () => {
    setIsCreating(true);
    setCreateError(null);

    try {
      const { data } = await axiosInstance.post("/activity-webhook", selection);
      createActivityWebhook(data);
      setSelection({ name: "", state_machine_id: "", activity_id: "" }); // reset form
    } catch (err) {
      setCreateError(handleApiError(err));
    } finally {
      setIsCreating(false);
    }
  };

  // Handle fetch states
  if (loading) return <div>Loading state machines...</div>;
  if (error) return <div className="text-red-500">Error: {error.message}</div>;
  if (!data || data.length === 0) return <div>No state machines found</div>;

  const activities = data.find((sm) => sm.id === selection.state_machine_id)?.activities || [];

  return (
    <div className="d_flex flex_column gap_2">
      <Input
        label="Webhook Name"
        type="text"
        value={selection.name}
        onChange={(e) => setSelection((prev) => ({ ...prev, name: e.target.value }))}
        placeholder="Enter webhook name"
        className="border rounded p-2 w-full"
      />

      <SingleSelectBox
        selectedData={data.find((sm) => sm.id === selection.state_machine_id) || null}
        options={data}
        labelKey={["name"]}
        onChange={(obj) =>
          setSelection({
            name: selection.name,
            state_machine_id: obj.id,
            activity_id: ""
          })
        }
        label="State Machine"
        placeholder="Select State Machine"
        isSearch={true}
      />

      <SingleSelectBox
        selectedData={activities.find((act) => act.id === selection.activity_id) || null}
        options={activities}
        labelKey={["name"]}
        onChange={(obj) => setSelection((prev) => ({ ...prev, activity_id: obj.id }))}
        label="Activity"
        placeholder="Select Activity"
        isSearch={true}
      />

      {createError && <div className="text-red-500 text-sm">{createError}</div>}

      <Button
        onClick={create}
        disabled={isCreating || !selection.name || !selection.state_machine_id || !selection.activity_id}
      >
        {isCreating ? "Creating..." : "Create Webhook"}
      </Button>
    </div>
  );
};

export default ApiSection;
